import { useRef } from "react";
import { FaCheckCircle, FaClipboardCheck, FaFileAlt, FaMapMarkerAlt, FaTools, FaUserTie } from "react-icons/fa";
import type { ExpertProfile } from "../../types/Profiles/expertProfile.types";
import { uploadExpertLogo } from "../../services/expertProfile/expertProfile";

const DEFAULT_LOGO = "/asset/images/default-oem.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://admin.aida-dz.xyz";
const API_ORIGIN = BACKEND_URL.replace(/\/api\/?$/i, "").replace(/\/$/, "");

const getLogoUrl = (logo?: string | null) => {
  if (!logo) return DEFAULT_LOGO;
  if (/^https?:\/\//i.test(logo)) return logo;
  const normalized = logo.startsWith("/") ? logo : `/${logo}`;
  if (normalized.startsWith("/uploads/")) return `${API_ORIGIN}/api${normalized}`;
  return `${API_ORIGIN}${normalized}`;
};

const calculateCompletion = (profile: ExpertProfile) => {
  const items = [
    !!profile.identity.companyName,
    !!profile.identity.contactEmail && !!profile.identity.contactPhone,
    !!profile.identity.wilaya || !!profile.identity.address,
    profile.domains.length > 0,
    profile.certifications.length > 0,
  ];
  return Math.round((items.filter(Boolean).length / items.length) * 100);
};

interface Props {
  profile: ExpertProfile;
  userId?: number | string | null;
  onEdit?: () => void;
  onProfileUpdated?: () => Promise<void> | void;
}

export default function ExpertProfileSidebarCard({ profile, userId, onEdit, onProfileUpdated }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const completion = calculateCompletion(profile);
  const progressColor = completion >= 80 ? "#22c55e" : completion >= 50 ? "#f59e0b" : "#ef4444";

  const completionItems = [
    { label: "Informations de l'entreprise", done: !!profile.identity.companyName },
    { label: "Informations de contact", done: !!profile.identity.contactEmail && !!profile.identity.contactPhone },
    { label: "Localisation renseignee", done: !!profile.identity.wilaya || !!profile.identity.address },
    { label: "Domaines d'expertise ajoutes", done: profile.domains.length > 0 },
    { label: "Certifications ajoutees", done: profile.certifications.length > 0 },
  ];

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadExpertLogo(file, userId);
      await onProfileUpdated?.();
      event.target.value = "";
    } catch (error) {
      console.error("Erreur upload logo expert:", error);
    }
  };

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
        <div style={{ height: 55, background: "linear-gradient(135deg, #f4b400, #ffcc33)" }} />

        <div className="card-body text-center p-4" style={{ marginTop: -60 }}>
          <div className="mb-3 d-flex justify-content-center">
            <div className="position-relative" style={{ width: 110, height: 110, overflow: "visible" }}>
              <div className="rounded-circle border border-4 border-white shadow-sm bg-white d-flex align-items-center justify-content-center" style={{ width: "100%", height: "100%", padding: 12 }}>
                <img
                  src={getLogoUrl(profile.identity.logo)}
                  onClick={() => fileInputRef.current?.click()}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = DEFAULT_LOGO;
                  }}
                  alt={profile.identity.companyName || "Expert logo"}
                  style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "pointer", display: "block" }}
                />
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-warning rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                style={{ position: "absolute", right: 2, bottom: 2, width: 32, height: 32, padding: 0, border: "2px solid #fff", zIndex: 3 }}
                title="Modifier le logo"
              >
                <i className="fas fa-pen" style={{ fontSize: 12 }} />
              </button>

              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
            </div>
          </div>

          <h3 className="fw-bold mb-1">{profile.identity.companyName || "Expert AIDA"}</h3>
          <p className="text-muted mb-2 d-flex align-items-center justify-content-center gap-2"><FaMapMarkerAlt size={13} /><span>{profile.identity.wilaya || profile.identity.location || "-"}</span></p>
          <p className="text-muted mb-4 d-flex align-items-center justify-content-center gap-2"><FaUserTie size={13} /><span>{profile.identity.expertType || "Expert"}</span></p>

          <div className="rounded-pill px-3 py-3 fw-semibold mb-4" style={{ background: "#e8f7ec", color: "#15803d", fontSize: "0.95rem" }}>
            <FaCheckCircle className="me-2" />
            Expert verifie
          </div>

          <div className="row g-3 mb-4">
            <div className="col-4"><div className="border rounded-4 p-3 h-100 bg-white"><div className="mb-2 text-warning"><FaTools size={18} /></div><h4 className="fw-bold mb-1">{profile.domains.length}</h4><small className="text-muted">Domaines</small></div></div>
            <div className="col-4"><div className="border rounded-4 p-3 h-100 bg-white"><div className="mb-2 text-danger"><FaClipboardCheck size={18} /></div><h4 className="fw-bold mb-1">{profile.certifications.length}</h4><small className="text-muted">Certifs</small></div></div>
            <div className="col-4"><div className="border rounded-4 p-3 h-100 bg-white"><div className="mb-2 text-success"><FaFileAlt size={18} /></div><h4 className="fw-bold mb-1">{profile.supportNeeds.length}</h4><small className="text-muted">Besoins</small></div></div>
          </div>

          {onEdit && <button type="button" className="btn btn-warning rounded-pill px-4" onClick={onEdit}>Modifier le profil</button>}
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-semibold mb-0">Completion du profil</h5>
            <span className="badge rounded-pill bg-warning-subtle text-warning">{completion}%</span>
          </div>
          <div className="progress mb-4" style={{ height: 10, borderRadius: 999, backgroundColor: "#f1f5f9" }}>
            <div className="progress-bar" role="progressbar" style={{ width: `${completion}%`, backgroundColor: progressColor, borderRadius: 999 }} aria-valuenow={completion} aria-valuemin={0} aria-valuemax={100} />
          </div>
          <ul className="mb-0 ps-0" style={{ listStyle: "none" }}>
            {completionItems.map((item) => (
              <li key={item.label} className="mb-2 d-flex align-items-center gap-2">
                <span style={{ color: item.done ? "#16a34a" : "#9ca3af", fontSize: 14 }}>✓</span>
                <span style={{ color: item.done ? "#111827" : "#9ca3af", textDecoration: item.done ? "none" : "line-through" }}>{item.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
